import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase, withRetry } from './supabase';
import { checkMfaStatus, checkTrustedDevice } from './mfaService';
import toast from 'react-hot-toast';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  isLoading: boolean;
  mfaRequired: boolean;
  mfaVerified: boolean;
  refreshMfaStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);

  const updateMfaState = useCallback(async (currentUser: User | null) => {
    if (!currentUser) {
      setMfaRequired(false);
      setMfaVerified(false);
      return;
    }

    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('MFA check timeout')), 5000)
      );

      const mfaCheck = async () => {
        const status = await checkMfaStatus();
        
        if (status.needsChallenge) {
          // Check if device is trusted — skip MFA if so
          const isTrusted = await checkTrustedDevice(currentUser.id);
          if (isTrusted) {
            setMfaRequired(false);
            setMfaVerified(true);
          } else {
            setMfaRequired(true);
            setMfaVerified(false);
          }
        } else {
          setMfaRequired(false);
          setMfaVerified(status.isVerified);
        }
      };

      await Promise.race([mfaCheck(), timeoutPromise]);
    } catch (error) {
      console.error('Error checking MFA status:', error);
      // On any error, allow access (don't block the app)
      setMfaRequired(false);
      setMfaVerified(false);
    }
  }, []);

  const refreshMfaStatus = useCallback(async () => {
    await updateMfaState(user);
  }, [user, updateMfaState]);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await withRetry(() => 
          supabase.auth.getSession(),
          {
            maxRetries: 3,
            errorMessage: 'Failed to initialize authentication'
          }
        );
        
        if (error) {
          console.error('Error getting session:', error);
          toast.error('Authentication error. Please try again.');
          return;
        }

        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
          // Don't await MFA check during init — let it run in background
          updateMfaState(session?.user ?? null).catch(err => {
            console.error('MFA init check failed:', err);
          });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        toast.error('Failed to initialize authentication. Please refresh the page.');
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
        // Run MFA check in background — don't block rendering
        updateMfaState(session?.user ?? null).catch(err => {
          console.error('MFA auth change check failed:', err);
        });
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [updateMfaState]);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await withRetry(() =>
        supabase.auth.signInWithPassword({
          email,
          password,
        }),
        {
          maxRetries: 3,
          errorMessage: 'Sign in failed'
        }
      );
      
      if (error) throw error;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { error } = await withRetry(() =>
        supabase.auth.signUp({
          email,
          password,
        }),
        {
          maxRetries: 3,
          errorMessage: 'Sign up failed'
        }
      );
      
      if (error) throw error;
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await withRetry(() =>
        supabase.auth.signOut(),
        {
          maxRetries: 3,
          errorMessage: 'Sign out failed'
        }
      );
      if (error) throw error;
      setMfaRequired(false);
      setMfaVerified(false);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ session, user, signIn, signUp, signOut, isLoading, mfaRequired, mfaVerified, refreshMfaStatus }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}